type UnknownEvent = Record<string, unknown>;
type UnknownHint = { originalException?: unknown } & Record<string, unknown>;

export type BeforeSendFn = (
  event: UnknownEvent,
  hint: UnknownHint,
) => UnknownEvent | null | PromiseLike<UnknownEvent | null>;

export type SharedSentryInput = {
  app: unknown;
  dsn: string | undefined;
  release?: string;
  dist?: string;
  environment?: string;
  sampleRate?: number;
  ignoreErrors?: (string | RegExp)[];
  beforeSend?: BeforeSendFn;
};

export const SHARED_IGNORE_ERRORS: (string | RegExp)[] = [
  /CanceledError/,
  "ERR_CANCELED",
];

export const isCanceledRejection = (error: unknown): boolean => {
  if (!error || typeof error !== "object") {
    return false;
  }
  const e = error as Record<string, unknown>;
  return (
    e.canceled === true ||
    e.code === "ERR_CANCELED" ||
    e.name === "CanceledError"
  );
};

export const isNetworkOrTimeoutError = (error: unknown): boolean => {
  if (!error || typeof error !== "object") {
    return false;
  }
  const e = error as Record<string, unknown>;
  return (
    e.code === "ERR_NETWORK" ||
    e.code === "ECONNABORTED" ||
    e.message === "Network Error"
  );
};

export const buildSentryConfig = ({
  app,
  dsn,
  release,
  dist,
  environment,
  sampleRate,
  ignoreErrors,
  beforeSend,
}: SharedSentryInput): Record<string, unknown> => {
  const mergedBeforeSend: BeforeSendFn = (event, hint) => {
    if (isCanceledRejection(hint?.originalException)) {
      return null;
    }
    return beforeSend ? beforeSend(event, hint) : event;
  };

  return {
    app,
    dsn,
    release,
    dist,
    environment,
    sampleRate,
    // Performance traces are disabled org-wide: tracing ships a span for every
    // transaction and burns through our Sentry event quota fast for near-zero
    // diagnostic value at our current scale. Flip per app if you genuinely
    // need performance data, and pair it with a realistic sample rate.
    tracesSampleRate: 0,
    maxBreadcrumbs: 50,
    ignoreErrors: [...SHARED_IGNORE_ERRORS, ...(ignoreErrors ?? [])],
    beforeSend: mergedBeforeSend,
  };
};
